import type { ApiServices } from '../lib/services';

export function createSwrFetcher(services: ApiServices) {
  return async function fetcher(key: unknown): Promise<unknown> {
    if (!key) return null;

    const keyStr = typeof key === 'string' ? key : Array.isArray(key) ? key[0] : null;
    if (!keyStr) return null;

    // Route to appropriate service based on key
    if (keyStr === '/app/v1/users/me') {
      return services.auth.getUser();
    }
    if (keyStr === '/app/v1/projects') {
      return services.projects.getProjects();
    }
    if (keyStr === '/app/v1/invoices') {
      const params = Array.isArray(key) ? key[1] : undefined;
      return services.invoices.getInvoices((params as { page?: number; limit?: number }) || {});
    }
    if (typeof keyStr === 'string' && keyStr.startsWith('/api/v1/invoices/')) {
      const id = keyStr.replace('/api/v1/invoices/', '');
      return services.invoices.getInvoice(id);
    }
    if (keyStr === '/app/v1/invoices/statistic') {
      return services.invoices.getInvoiceStats();
    }
    if (keyStr === '/app/v1/wallets') {
      const params = Array.isArray(key) ? key[1] : undefined;
      const query = (params as Record<string, unknown>) || {};
      if (query.__legacy === true) {
        const legacyParams = { ...query };
        delete legacyParams.__legacy;
        return services.wallets.getWallets(
          legacyParams as import('../contracts').WalletsListParams,
        );
      }
      return services.wallets.getWalletsMonitoring(
        query as import('../contracts').WalletsMonitoringQuery,
      );
    }
    if (keyStr === '/app/v1/wallets/groups') {
      return services.wallets.getWalletGroups();
    }
    if (
      typeof keyStr === 'string' &&
      keyStr.startsWith('/app/v1/wallets/') &&
      keyStr.endsWith('/addresses')
    ) {
      const walletId = keyStr.slice('/app/v1/wallets/'.length, -'/addresses'.length);
      const params = Array.isArray(key) ? key[1] : undefined;
      return services.wallets.getWalletAddresses(
        walletId,
        (params as import('../contracts').WalletAddressesMonitoringQuery) || {},
      );
    }
    if (keyStr === '/app/v1/withdrawals') {
      const params = Array.isArray(key) ? key[1] : undefined;
      return services.withdrawals.getWithdrawals((params as { page?: number; limit?: number; status?: string }) || {});
    }
    if (keyStr === '/app/v1/withdrawals/admin') {
      const params = Array.isArray(key) ? key[1] : undefined;
      return services.withdrawals.getAdminWithdrawals((params as { page?: number; limit?: number; status?: string }) || {});
    }
    if (keyStr === '/app/v1/balances') {
      return services.accounting.getBalance();
    }
    if (keyStr === '/app/v1/balances/accountings') {
      const params = Array.isArray(key) ? key[1] : undefined;
      return services.accounting.getAccountings((params as { page?: number; limit?: number }) || {});
    }
    if (keyStr === '/app/v1/balances/accountings/service') {
      const params = Array.isArray(key) ? key[1] : undefined;
      return services.accounting.getServiceAccountings(
        (params as import('../contracts').ServiceAccountingsParams) || {}
      );
    }
    if (keyStr === '/app/v1/balances/accountings/partners') {
      const params = Array.isArray(key) ? key[1] : undefined;
      return services.referral.getPartnersAccountings((params as { page?: number; limit?: number }) || {});
    }
    if (keyStr === '/app/v1/balances/partners') {
      return services.referral.getPartnersBalance();
    }
    if (keyStr === '/app/v1/meta/partners-count') {
      return services.meta.getPartnersCount();
    }
    if (keyStr === '/app/v1/meta/currencies') {
      return services.meta.getCurrencies();
    }
    if (keyStr === '/app/v1/address-aml') {
      const params = Array.isArray(key) ? key[1] : undefined;
      return services.addressAml.getAddressAmls(
        (params as import('../contracts').AddressAmlListParams) || {}
      );
    }
    if (keyStr === '/app/v1/address') {
      const params = Array.isArray(key) ? key[1] : undefined;
      return services.addressAml.getAddresses(
        (params as import('../contracts').AddressCatalogListParams) || {}
      );
    }
    if (keyStr === '/app/v1/audit-logs') {
      const search = Array.isArray(key) ? String(key[1] ?? '') : '';
      return services.auditLog.list(search);
    }
    if (typeof keyStr === 'string' && keyStr.startsWith('/app/v1/audit-logs/')) {
      const id = decodeURIComponent(keyStr.slice('/app/v1/audit-logs/'.length));
      return services.auditLog.getById(id);
    }
    if (keyStr === '/app/v1/admin/compliance/withdrawals') {
      const params = Array.isArray(key) ? (key[1] as { status?: string; page?: number; limit?: number }) : undefined;
      return services.compliance.listWithdrawals(params || {});
    }
    if (typeof keyStr === 'string' && keyStr.startsWith('/app/v1/admin/compliance/withdrawals/')) {
      return services.compliance.getWithdrawal(keyStr.slice('/app/v1/admin/compliance/withdrawals/'.length));
    }
    if (keyStr === '/app/v1/admin/compliance/decisions') return services.compliance.listPendingDecisions();
    if (keyStr === '/app/v1/admin/compliance/addresses') return services.compliance.listBlockedAddresses();
    if (keyStr === '/app/v1/admin/compliance/reviewers') return services.compliance.listReviewers();
    if (keyStr === '/app/v1/admin/aml-risk-policy') return services.aml.getRiskPolicy();
    if (keyStr === '/app/v1/admin/aml-billing/settings') return services.aml.getBillingSettings();
    if (keyStr === '/app/v1/admin/aml-charges/summary') {
      const month = Array.isArray(key) ? String(key[1] ?? '') : '';
      return services.aml.getChargesSummary(month);
    }
    if (keyStr === '/app/v1/admin/aml-charges') {
      const month = Array.isArray(key) ? String(key[1] ?? '') : '';
      const merchantId = Array.isArray(key) ? String(key[2] ?? '') : '';
      return services.aml.getAdminCharges(month, merchantId || undefined);
    }
    if (keyStr === '/app/v1/aml-charges') {
      const month = Array.isArray(key) ? String(key[1] ?? '') : '';
      return services.aml.getMyCharges(month);
    }

    return null;
  };
}

export type SwrFetcher = ReturnType<typeof createSwrFetcher>;
